import { Col, Breadcrumb, BreadcrumbItem, Badge, Button } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Popover, Whisper } from "rsuite";
import { useState } from "react";

import DropdownOptionWithIcon from "../../../components/Menus/DropdownOptionWithIcon";
import PopoverDropdownMenu from "../../../components/Menus/PopoverDropdownMenu";
import { newToastEvent, updateToastEvent } from "../../../utils/ToastUtil";
import useUpdateCloud from "../../../api/api-hooks/cloud/useUpdateCloud";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { Tenant } from "../../../types/GlobalInterfaces";
import { AppConfirm } from "../../../utils/PopupUtil";
import CloudTemplateModal from "./CloudTemplateModal";
import RenameCloudModal from "./RenameCloudModal";
import Constants from "../../../utils/Constants";
import API from "../../../api/api/API";

interface Props {
  cloud: Tenant | undefined;
  loading: boolean;
  urlStart?: string;
  mode: "mc" | "supp";
}

const CloudDetailHeader = ({ cloud, loading, urlStart = "", mode }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [renameModal, setRenameModal] = useState(false);
  const [createTemplateModal, setCreateTemplateModal] = useState(false);
  const { updateCloud } = useUpdateCloud();
  const isGlobal = !!cloud?.Global;
  const brieDisabled = !!cloud?.BrieDisabled;

  return (
    <>
      <div className="tw:w-full tw:mb-1 tw:mt-2 tw:flex tw:items-center tw:justify-between">
        <div className="align-items-center py-4">
          <Col xs="">
            <div className="d-flex align-items-center">
              {isGlobal && <Badge color="primary">Global</Badge>}
              <h6 className="h2 mb-0 ml-2">
                {loading ? <SkeletonLoader width="200px" /> : cloud?.Name}
              </h6>
            </div>
            <Breadcrumb
              className="d-block mt-1"
              listClassName="breadcrumb-links"
            >
              <BreadcrumbItem>
                <Link to={urlStart}>
                  <i className="mdi mdi-lifebuoy" />
                </Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <Link to={`${urlStart}`}>Clouds</Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                {loading ? "Loading..." : cloud?.Name}
              </BreadcrumbItem>
            </Breadcrumb>
          </Col>
        </div>
        {mode === "mc" && (
          <div className="tw:mr-10">
            <CloudDetailOptions
              deleteCloud={deleteCloud}
              disabled={
                loading ||
                [Constants.SUP_CLOUD_ID, Constants.MC_CLOUD_ID].includes(
                  cloud?.id ?? ""
                )
              }
              brieDisabled={brieDisabled}
              toggleBrieActivation={toggleBrieActivation}
              addGlobalToCloud={addGlobalToCloud}
              removeGlobalFromCloud={removeGlobalFromCloud}
              isGlobal={isGlobal}
            />
          </div>
        )}
      </div>
      <RenameCloudModal
        open={renameModal}
        onClose={() => setRenameModal(false)}
        cloud={cloud}
      />
      <CloudTemplateModal
        open={createTemplateModal}
        onClose={() => setCreateTemplateModal(false)}
        cloud={cloud}
      />
    </>
  );

  async function toggleBrieActivation() {
    const text = brieDisabled ? "Enable" : "Disable";
    const description = brieDisabled
      ? "This will allow all users of this cloud to have access to Brie."
      : "This will prevent all users of this cloud from using Brie.";

    const result = await AppConfirm({
      title: `${text} Brie Tools`,
      confirmButtonColor: brieDisabled ? "var(--success)" : "var(--danger)",
      text: `Are you sure you want to ${text} Brie Tools in this Cloud? ${description}`,
      confirmButtonText: `${text} Brie`,
    });

    if (!result.isConfirmed || !cloud?.id) return;

    updateCloud({
      tenantID: cloud.id,
      payload: { BrieDisabled: !cloud?.BrieDisabled },
    });
  }

  async function addGlobalToCloud() {
    const result = await AppConfirm({
      title: "Set Cloud as Global?",
      text: `Are you sure you want to set this Cloud as Global? This will allow it to have "Global Apps" that can be installed in ANY cloud.`,
      confirmButtonText: "Set as Global",
    });

    if (!result.isConfirmed || !cloud?.id) return;

    updateCloud({ tenantID: cloud.id, payload: { Global: true } });
  }

  async function removeGlobalFromCloud() {
    const result = await AppConfirm({
      title: "Remove Global?",
      text: `Are you sure you want to remove Global capabilities from this cloud? This will hide all of your "Global Apps" from other clouds.`,
      confirmButtonText: "Remove Global",
    });

    if (!result.isConfirmed || !cloud?.id) return;

    updateCloud({ tenantID: cloud.id, payload: { Global: false } });
  }

  function deleteCloud() {
    AppConfirm({
      title: `Delete '${cloud?.Name}' Cloud?`,
      text: `Are you sure you want to permanently delete '${cloud?.Name}' Cloud? This action cannot be undone.`,
    }).then((result) => {
      if (!result.isConfirmed) return;

      newToastEvent("success", "Deleting Cloud...", "DeletingCloud", {
        autoClose: false,
        loading: true,
      });

      API.deleteCloudPermanent(cloud?.id ?? "").then(
        () => {
          updateToastEvent(
            "success",
            "Successfully deleted cloud.",
            "DeletingCloud",
            {
              loading: false,
              autoClose: true,
            }
          );

          let matchedQueryKeys: any[] = [];

          queryClient.setQueriesData<{ records: any[] }>(
            {
              predicate: ({ queryKey }) => {
                const matched = queryKey.some((key) => key === "Tenants");
                if (matched) matchedQueryKeys.push(queryKey);

                return matched;
              },
            },
            (prev) => prev
          );

          for (const key of matchedQueryKeys) {
            queryClient.invalidateQueries(key);
          }

          AppConfirm({
            title: `Cloud Deleted`,
            text: `The cloud has been successfully deleted.`,
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary)",
            showCancelButton: false,
          }).then(() => {
            navigate(urlStart);
          });
        },
        (error) => {
          newToastEvent(
            "error",
            "We were unable to delete the cloud. Please try again."
          );
          console.error(error);
        }
      );
    });
  }
};

const CloudDetailOptions = ({
  brieDisabled,
  disabled,
  isGlobal,
  deleteCloud,
  toggleBrieActivation,
  addGlobalToCloud,
  removeGlobalFromCloud,
}: {
  disabled: boolean;
  brieDisabled: boolean;
  isGlobal: boolean;
  deleteCloud: () => void;
  toggleBrieActivation: () => void;
  addGlobalToCloud: () => void;
  removeGlobalFromCloud: () => void;
}) => {
  return (
    <Whisper
      trigger="click"
      placement="bottomEnd"
      speaker={
        <Popover className="sidebar-popover">
          <PopoverDropdownMenu>
            <DropdownOptionWithIcon
              name={brieDisabled ? "Enable Brie" : "Disable Brie"}
              onClick={toggleBrieActivation}
              icon="mdi mdi-face-agent"
            />
            {!isGlobal && (
              <DropdownOptionWithIcon
                name="Set as Global Cloud"
                onClick={addGlobalToCloud}
                icon="mdi mdi-earth"
              />
            )}
            {isGlobal && (
              <DropdownOptionWithIcon
                name="Remove Global Cloud"
                onClick={removeGlobalFromCloud}
                icon="mdi mdi-earth-off"
              />
            )}
            <DropdownOptionWithIcon
              name="Delete Cloud"
              onClick={deleteCloud}
              icon="mdi mdi-delete"
            />
          </PopoverDropdownMenu>
        </Popover>
      }
    >
      <Button disabled={disabled}>
        <span className="tw:ml-2">Actions</span>
        <i className="mdi mdi-chevron-down" />
      </Button>
    </Whisper>
  );
};

export default CloudDetailHeader;
